import type { TrackLevel } from '@verybigthings/trackframe-shared/schemas';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';

/**
 * The two grant fields every "give a user access" dialog shares (Add user,
 * Edit access, Invite by email — HUB-109 D7), plus the two read-only cells
 * (level badge, role chips) the Access and user Tracks tables share. Levels
 * are shown as `admin | member | viewer`, roles by name — never a relation
 * string.
 */

export const ROLE_ONLY_TEXT = '— (role only)';

export interface RoleRef {
  id: string;
  name: string;
}

export function LevelSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: TrackLevel | null;
  onChange: (level: TrackLevel | null) => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>Level</Label>
      <Select
        id={id}
        className="w-full"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : (e.target.value as TrackLevel))}
      >
        <option value="">None — roles only</option>
        <option value="viewer">viewer — all read tools</option>
        <option value="member">member — read and write tools</option>
        <option value="admin">admin — manages this track</option>
      </Select>
    </div>
  );
}

export function RoleCheckList({
  roles,
  selected,
  onToggle,
}: {
  roles: RoleRef[];
  selected: Set<string>;
  onToggle: (roleId: string, checked: boolean) => void;
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Roles</p>
      {roles.length === 0 ? (
        <p className="text-xs text-muted-foreground">No roles on this track yet.</p>
      ) : (
        roles.map((role) => (
          <label key={role.id} className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={selected.has(role.id)}
              onCheckedChange={(checked) => onToggle(role.id, checked)}
              aria-label={role.name}
            />
            {role.name}
          </label>
        ))
      )}
    </div>
  );
}

export function toggleInSet(set: Set<string>, id: string, on: boolean): Set<string> {
  const next = new Set(set);
  if (on) next.add(id);
  else next.delete(id);
  return next;
}

export function LevelCell({ level }: { level: TrackLevel | null }) {
  if (!level) return <span className="text-xs text-muted-foreground">{ROLE_ONLY_TEXT}</span>;
  return <Badge variant={level === 'admin' ? 'default' : 'outline'}>{level}</Badge>;
}

export function RoleChips({ roles }: { roles: RoleRef[] }) {
  if (roles.length === 0) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {roles.map((role) => (
        <Badge key={role.id} variant="outline">
          {role.name}
        </Badge>
      ))}
    </div>
  );
}
